import { useEffect, useState } from "react"
import { Button, CircularProgress, Paper, Typography } from "@mui/material"
import { useSelector } from "react-redux";
import { selectUser } from "../../reducers/userSlice";
import { getRTFOCompanyName, isRTFOAdminUser, isRTFOSuperUser } from "./rtfoConfig";
import { useSocket } from "../../context/useSocket";
import { PlayCircle, Refresh, StopCircle } from "@mui/icons-material";
import { useTheme } from "@mui/styles";

const ENV = process.env.REACT_APP_NODE_ENV;
const BASE_URL_SSD = ENV === 'production' ? process.env.REACT_APP_BASE_URL_SSD : (ENV === 'staging' || ENV === 'development' ) ? process.env.REACT_APP_BASE_URL_SSD_STAGING : 'http://localhost:8080/api';

export const RTFOAdminContainer = () => {
  const { socket } = useSocket();
  const theme = useTheme();

  const user = useSelector(selectUser);
  const darkMode = user?.value?.darkMode;
  const userEmail = user?.value?.email;
  const isRTFOAdmin = isRTFOAdminUser(userEmail);
  const isRTFOSuper = isRTFOSuperUser(userEmail);


  // if user is not admin, route them back to rtfo page
  if (!isRTFOAdmin || !isRTFOSuper) {
    let newURL = window.location.href;
    newURL = newURL.replace('/admin', '');
    window.location.href = newURL;
  };

  // get pad activity for active pads only
  const [isLoading, setIsLoading] = useState(true);
  const [activePads, setActivePads] = useState([]);
  const [processingPads, setProcessingPads] = useState({});
  const [padStages, setPadStages] = useState({});
  const [lastStatusUpdate, setLastStatusUpdate] = useState(null);


  // Fetch active pads
  useEffect(() => {
    
    if (activePads?.length > 0) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      try {
        const response = await fetch(`${BASE_URL_SSD}/rtfo/activity`, { 
          method: 'GET',
          headers: {
            'x-api-key': process.env.REACT_APP_API_KEY,
            client: 'ssd'
          },
          signal });
        if (!response.ok) {
          throw new Error('Network response was not ok');
        };
        const result = await response.json();
        const activePads = result?.filter(pad => pad?.isActive);

        activePads.forEach(pad => {
          setPadStages(prev => ({...prev, [pad.name]: pad.stages}));
        });

        setActivePads(activePads);
        setIsLoading(false);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.log(err);
          setIsLoading(false);
        };
      };
    };

    fetchData();

    // Cleanup function to abort fetch on unmount
    return () => {
      controller.abort();
    };
  }, [activePads?.length, padStages]);

  // stop processing on websocket disconnect
  useEffect(() => {

    const onDisconnect = () => {
      setProcessingPads({});
    };

    socket?.addEventListener('close', onDisconnect);

    return () => {
      socket?.removeEventListener('close', onDisconnect);
    };

  }, [socket]);

  if (socket) {
    socket.onmessage = (event) => {
      const response = JSON.parse(event?.data);
  
      if (!response) return;
  
      if (response.messageType === 'processingPads') {
        setProcessingPads(response.payload);
        setLastStatusUpdate(new Date());
      } else if (response.messageType === 'stageData') {
        
        const padName = response?.payload?.padName;
        const stageData = response?.payload?.stageData;

        console.log('Received response for pad', padName, stageData?.name);

        console.log(stageData)

        // detect new stages
        if (stageData) {
          if (!padStages[padName]?.includes(stageData?.name)) {
            setPadStages(prev => ({...prev, [padName]: [...prev[padName].stages, stageData?.name]}));
          };
        };

        if (padName && processingPads[padName]) {
          const timeout = (!stageData) ? 30000 : 500;
          setTimeout(() => {
            console.log(`Sending stage process request for pad ${padName}...`);
            socket?.send(JSON.stringify({ // send latest stage process request
              messageType: 'processStage',
              padName,
              userEmail
            }));
          }, timeout);
        };

        setProcessingPads(prev => ({...prev, [padName]: new Date()}));
        setLastStatusUpdate(new Date());
      };
    };
  };

  return (
    <div style={{width: '100%', height: '100%', paddingTop: 70, paddingLeft: 90, paddingRight: 30, backgroundColor: theme.palette.paper[darkMode ? 'dark' : 'light']}}>
      {isLoading ? 
        <div style={{width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
          <CircularProgress style={{color: darkMode ? 'white' : theme.palette.primary.main}} />
        </div>  :
        <div>
          <Typography variant="h5">RTFO Pad Management</Typography>

          <Typography>Last update: {lastStatusUpdate ? lastStatusUpdate?.toLocaleString() : ''}</Typography>

          <Button startIcon={<Refresh />} onClick={() => getProcessingPads(socket)}>Refresh</Button>

          <div style={{display: 'flex', flexDirection: 'column', padding: 20, width: '100%', height: 'calc(100%-500px)', justifyContent: 'center', alignItems: 'center', backgroundColor: 'transparent'}}>
            {activePads?.map(pad => {
              return (
                <PadItem pad={pad} socket={socket} padStages={padStages} processingPads={processingPads} setProcessingPads={setProcessingPads} key={pad?.name} theme={theme} darkMode={darkMode} userEmail={userEmail} />
              )
            })}

          </div>
        </div>
      } 
    </div>
  );
};

function PadItem({ pad, socket, padStages, processingPads, setProcessingPads, theme, darkMode, userEmail }) {
  const isPadProcessing = processingPads?.[pad?.name];
  return (
    <Paper style={{width: '100%', height: 60, padding: '0 10px', backgroundColor: theme.palette.elevationOne[darkMode ? 'dark' : 'light'], display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 10}}>
      <div style={{width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'}}>
        <div style={{display: 'flex', flexDirection: 'column', width: 100, margin: '0 10px'}}>
          <Typography variant="caption" color="textSecondary">Pad Name</Typography>
          <Typography variant="body">{pad?.name}</Typography>
        </div>
        <div style={{display: 'flex', flexDirection: 'column', width: 200, margin: '0 10px'}}>
          <Typography variant="caption" color="textSecondary">Client</Typography>
          <Typography variant="body">{getRTFOCompanyName(pad?.client)}</Typography>
        </div>
        <div style={{display: 'flex', flexDirection: 'column', width: 150, margin: '0 10px'}}>
          <Typography variant="caption" color="textSecondary">Formation</Typography>
          <Typography variant="body">{pad?.formation}</Typography>
        </div>
        <div style={{display: 'flex', flexDirection: 'column', width: 120, margin: '0 10px'}}>
          <Typography variant="caption" color="textSecondary">Area</Typography>
          <Typography variant="body">{pad?.area}</Typography>
        </div>
        <div style={{display: 'flex', flexDirection: 'column', width: 50, margin: '0 10px'}}>
          <Typography variant="caption" color="textSecondary">Wells</Typography>
          <Typography variant="body">{pad?.perfSchedule?.wells ? Object.keys(pad?.perfSchedule?.wells)?.length : 0}</Typography>
        </div>
        <div style={{display: 'flex', flexDirection: 'column', width: 150, margin: '0 10px'}}>
          <Typography variant="caption" color="textSecondary">Stages</Typography>
          <Typography variant="body">{padStages[pad?.name]?.length} / {getTotalStages(pad?.perfSchedule)} ({(padStages[pad?.name]?.length / getTotalStages(pad?.perfSchedule) * 100)?.toFixed(1)}%)</Typography>
        </div>
        <div style={{display: 'flex', flexDirection: 'column', width: 100, margin: '0 10px'}}>
          <Typography variant="caption" color="textSecondary">Status</Typography>
          <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
            <div style={{width: 16, height: 16, borderRadius: 8, backgroundColor: isPadProcessing ? 'green' : 'tomato', marginRight: 5}}></div>
            <Typography variant="body">{isPadProcessing ? 'ACTIVE' : 'IDLE'}</Typography>
          </div>
        </div>
        <div style={{display: 'flex', flexDirection: 'column', width: 200, margin: '0 10px'}}>
          <Typography variant="caption" color="textSecondary">Last Updated</Typography>
          <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
            <Typography variant="body">{isPadProcessing ? new Date(processingPads?.[pad?.name])?.toLocaleString() : 'N/A'}</Typography>
          </div>
        </div>

      </div>

      <Button 
        startIcon={isPadProcessing ? <StopCircle /> : <PlayCircle />} 
        onClick={() => handleStartStopProcessing(pad?.name, processingPads, setProcessingPads, socket, userEmail)}
        color={darkMode ? "inherit" : "primary"}
        style={{width: 100}}
      >
        {isPadProcessing ? 'Stop' : 'Start'}
      </Button>

    </Paper>
  );
};

function getTotalStages(perfSchedule) {
  if (!perfSchedule?.wells) return 0;
  return Object.values(perfSchedule.wells)?.map(w => Object.keys(w.stages))?.flat(Infinity)?.length || 0;
};

function getProcessingPads(socket) {
  if (socket?.readyState === 1) {
    socket.send(JSON.stringify({
      messageType: 'getProcessingPads'
    }));
  };
  return;
};

function handleStartStopProcessing(padName, processingPads, setProcessingPads, socket, userEmail) {
  if (socket?.readyState === 1) {
    
    const isPadProcessing = processingPads[padName];
    if (isPadProcessing) {
      console.log('Stopping processing of pad', padName);
      setProcessingPads(prev => {
        const newProcessingPads = {...prev};
        delete newProcessingPads[padName];
        return newProcessingPads;
      });
      socket?.send(JSON.stringify({
        messageType: 'stopPadMonitoring',
        padName,
        userEmail
      }));
    } else {
      console.log('Starting processing of pad', padName);
      setProcessingPads(prev => ({...prev, [padName]: new Date()}));
      socket?.send(JSON.stringify({
        messageType: 'processStage',
        padName,
        userEmail
      }));
    };
  };

  return;
};